import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@/lib/use-toast";
import type { MeasurementGroup, MeasurementGroupCreate, MeasurementGroupField, MeasurementGroupFieldCreate, MeasurementGroupFieldUpdate, MeasurementGroupWithFields, MeasurementTemplate } from "@/types/measurement";
import { measurementFieldQueryKeys } from "./measurement-fields";
import { addFieldsToGroup, archiveMeasurementGroup, createFromTemplate, createMeasurementGroup, listMeasurementGroups, listTemplates, removeChildGroupFromGroup, removeFieldFromGroup, updateGroupField, updateMeasurementGroup } from "@/api/provider/settings/measurement-group";
import { invalidateByKey } from "@/api/utils/query-helpers";
export const measurementGroupQueryKeys = {
  all: ["settings", "measurement-groups"] as const,
  list: () => [...measurementGroupQueryKeys.all, "list"] as const,
  templates: () => [...measurementGroupQueryKeys.all, "templates"] as const
};
export const measurementGroupQueryOptions = {
  list: () => ({
    queryKey: measurementGroupQueryKeys.list(),
    queryFn: () => listMeasurementGroups(),
    select: (data: MeasurementGroupWithFields[] | null) => data ?? []
  }),
  templates: () => ({
    queryKey: measurementGroupQueryKeys.templates(),
    queryFn: () => listTemplates(),
    select: (data: MeasurementTemplate[] | null) => data ?? []
  })
};
export const useCreateGroupMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onError: _temp2,
      onSettled: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateGroupMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp4,
      onError: _temp5,
      onSettled: _temp6
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useArchiveGroupMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp7,
      onError: _temp8,
      onSettled: _temp9
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useAddFieldsToGroupMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp10,
      onError: _temp11,
      onSettled: _temp12
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateGroupFieldMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp13,
      onError: _temp14,
      onSettled: _temp15
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useRemoveFieldFromGroupMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp16,
      onError: _temp17,
      onSettled: _temp18
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useRemoveChildGroupFromGroupMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp19,
      onError: _temp20,
      onSettled: _temp21
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useCreateFromTemplateMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp22,
      onSuccess: _temp23,
      onError: _temp24,
      onSettled: _temp25
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
function _temp(data) {
  return createMeasurementGroup(data);
}
function _temp2() {
  toast({
    title: "Error",
    description: "Failed to create group",
    variant: "error"
  });
}
function _temp3() {
  invalidateByKey(measurementGroupQueryKeys.list());
}
function _temp4(t0) {
  const {
    groupId,
    data
  } = t0;
  return updateMeasurementGroup(groupId, data);
}
function _temp5() {
  toast({
    title: "Error",
    description: "Failed to update group",
    variant: "error"
  });
}
function _temp6() {
  invalidateByKey(measurementGroupQueryKeys.list());
}
function _temp7(groupId) {
  return archiveMeasurementGroup(groupId);
}
function _temp8() {
  toast({
    title: "Error",
    description: "Failed to delete group",
    variant: "error"
  });
}
function _temp9() {
  invalidateByKey(measurementGroupQueryKeys.list());
}
function _temp10(t0) {
  const {
    groupId,
    fields
  } = t0;
  return addFieldsToGroup(groupId, fields);
}
function _temp11() {
  toast({
    title: "Error",
    description: "Failed to add fields to group",
    variant: "error"
  });
}
function _temp12() {
  invalidateByKey(measurementGroupQueryKeys.list());
}
function _temp13(t0) {
  const {
    groupId,
    fieldId,
    data
  } = t0;
  return updateGroupField(groupId, fieldId, data);
}
function _temp14() {
  toast({
    title: "Error",
    description: "Failed to update field",
    variant: "error"
  });
}
function _temp15() {
  invalidateByKey(measurementGroupQueryKeys.list());
}
function _temp16(t0) {
  const {
    groupId,
    fieldId
  } = t0;
  return removeFieldFromGroup(groupId, fieldId);
}
function _temp17() {
  toast({
    title: "Error",
    description: "Failed to remove field",
    variant: "error"
  });
}
function _temp18() {
  invalidateByKey(measurementGroupQueryKeys.list());
}
function _temp19(t0) {
  const {
    groupId,
    childGroupId
  } = t0;
  return removeChildGroupFromGroup(groupId, childGroupId);
}
function _temp20() {
  toast({
    title: "Error",
    description: "Failed to remove child group",
    variant: "error"
  });
}
function _temp21() {
  invalidateByKey(measurementGroupQueryKeys.list());
}
function _temp22(templateName) {
  return createFromTemplate(templateName);
}
function _temp23() {
  toast({
    title: "Success",
    description: "Template applied successfully"
  });
}
function _temp24() {
  toast({
    title: "Error",
    description: "Failed to apply template",
    variant: "error"
  });
}
function _temp25() {
  invalidateByKey(measurementFieldQueryKeys.list());
  invalidateByKey(measurementGroupQueryKeys.list());
}